// Is this browser ours?
//
// Over 2026-07-25..08-03 GA4 reported 111 sessions with sessionMedium=cpc while
// Google Ads billed 79 clicks. The 73 sessions carrying campaign id 24058829757
// reconciled with those clicks; the other 38 carried no campaign id, and 36 of
// them came from a single city on two devices. They were almost certainly our
// own — but "almost certainly" is a guess about geography, and nothing in the
// event payload could separate them from customers. A funnel measured on a 52%
// inflated denominator is not a funnel.
//
// This is the fix, and it is deliberately the dull one: a browser counts as
// internal only when someone explicitly said so by visiting with ?fp_internal=1.
// No IP heuristic, no city list, no user-agent sniffing — each of those would
// silently mislabel a real customer, and a wrongly-excluded customer is a worse
// error than an included one.
//
// Turn on:  https://filterproof.ai/login?fp_internal=1
// Turn off: https://filterproof.ai/login?fp_internal=0
//
// The mark is per-browser-profile and survives sessions, because that matches
// what it describes: a device that belongs to us. It is not personal data — it
// records nothing about who is using the browser, only that the browser is not
// a customer's.

const KEY = "fp_traffic_type";
export const INTERNAL = "internal";
export const EXTERNAL = "external";

// Reading the flag out of the URL happens here rather than in an init call, so
// there is no ordering rule to get wrong: whoever asks first also applies it.
function applyQueryOverride() {
  try {
    if (typeof window === "undefined" || !window.location) return;
    const raw = new URLSearchParams(window.location.search).get("fp_internal");
    if (raw === null) return;
    // Anything other than an explicit truthy value clears the mark. "?fp_internal"
    // with no value, "0", "false" — all mean "this is not our browser", because
    // the only safe default for an ambiguous signal is to count as a customer.
    if (raw === "1" || raw === "true") localStorage.setItem(KEY, INTERNAL);
    else localStorage.removeItem(KEY);
  } catch { /* private mode / storage disabled — treated as external below */ }
}

/**
 * "internal" | "external". Never throws: a browser that denies storage is a
 * customer's browser far more often than it is ours, so it reads as external.
 */
export function trafficType() {
  applyQueryOverride();
  try {
    return localStorage.getItem(KEY) === INTERNAL ? INTERNAL : EXTERNAL;
  } catch {
    return EXTERNAL;
  }
}

export function isInternalTraffic() {
  return trafficType() === INTERNAL;
}

/**
 * Coarse band for how long a visitor waited before the app was usable.
 *
 * Boundaries come from what production actually does rather than from a style
 * guide: measured against filterproof.ai on 2026-08-04, a Pixel-7 profile on a
 * 4G connection reached a clickable sign-in button at ~2.7s and the same profile
 * on a throttled connection at ~5.7s, against a desktop baseline of ~0.75s. So
 * "fast" is the desktop case, "ok" is a good mobile connection, "slow" is a poor
 * one, and "very_slow" is past the point where the ad click is almost certainly
 * already lost.
 */
export function readyBand(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return "unknown";
  if (n < 1000) return "fast";
  if (n < 3000) return "ok";
  if (n < 6000) return "slow";
  return "very_slow";
}
